import { SettingOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Flex, Layout, Space, Steps, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { StartupEmailStep } from '@/app/session/StartupEmailStep';
import { StartupLocationStep } from '@/app/session/StartupLocationStep';
import { StartupSessionProvider } from '@/app/session/StartupSessionContext';
import { isValidEmail } from '@/app/session/startup-session';
import { STARTUP_STEPS, type StartupStep, useStartupGateState } from '@/app/session/useStartupGateState';
import { inventoryRoutes } from '@/app/shell/routes';
import type { InventoryLocale } from '@/shared/i18n/inventory-i18n';
import { LanguageSwitcher } from '@/shared/i18n/LanguageSwitcher';
import { messages } from './messages';

interface StartupGateProps {
  children: ReactNode;
  locale: InventoryLocale;
  onLocaleChange: (locale: InventoryLocale) => void;
}

const startupStepIndex: Record<StartupStep, number> = {
  [STARTUP_STEPS.email]: 0,
  [STARTUP_STEPS.location]: 1,
  [STARTUP_STEPS.ready]: 1,
};

export function StartupGate({ children, locale, onLocaleChange }: StartupGateProps) {
  const intl = useIntl();
  const startup = useStartupGateState();

  if (startup.step === STARTUP_STEPS.ready) {
    return (
      <StartupSessionProvider
        onChangeLocation={() => startup.setStep(STARTUP_STEPS.location)}
        snapshot={startup.snapshot}
      >
        {children}
      </StartupSessionProvider>
    );
  }

  const heading =
    startup.step === STARTUP_STEPS.email
      ? intl.formatMessage(messages.startupYourEmail)
      : intl.formatMessage(messages.startupChooseLocation);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Content style={{ padding: 16 }}>
        <Flex align="flex-start" justify="center">
          <Space orientation="vertical" size="small" style={{ maxWidth: 560, width: '100%' }}>
            <Flex justify="flex-end">
              <Space size="small">
                <LanguageSwitcher compact onChange={onLocaleChange} value={locale} />
                <NavLink to={inventoryRoutes.settings}>
                  <Button aria-label={intl.formatMessage(messages.shellSettings)} icon={<SettingOutlined />} />
                </NavLink>
              </Space>
            </Flex>

            <Card>
              <Space orientation="vertical" size="large" style={{ width: '100%' }}>
                <Steps
                  current={startupStepIndex[startup.step]}
                  items={[
                    { title: intl.formatMessage(messages.appAuthPageEmail) },
                    {
                      title: intl.formatMessage(messages.startupLocationStep),
                    },
                  ]}
                  responsive={false}
                  size="small"
                  style={{ width: '100%' }}
                />

                <div>
                  <Typography.Title id="startup-heading" level={1}>
                    {heading}
                  </Typography.Title>
                </div>

                {startup.error ? <Alert message={startup.error} showIcon type="error" /> : null}
                {startup.isValidatingLocation ? (
                  <Alert message={intl.formatMessage(messages.startupValidatingLocationApiKey)} showIcon type="info" />
                ) : null}

                {startup.step === STARTUP_STEPS.email ? (
                  <StartupEmailStep
                    email={startup.email}
                    isEmailValid={isValidEmail(startup.email)}
                    onChange={startup.setEmail}
                    onSubmit={startup.submitEmail}
                  />
                ) : (
                  <StartupLocationStep
                    activeLocation={startup.activeLocation}
                    apiKey={startup.apiKey}
                    isAddLocationOpen={startup.isAddLocationOpen}
                    isValidatingLocation={startup.isValidatingLocation}
                    locationApiKeyError={startup.locationApiKeyError}
                    locations={startup.snapshot.locations}
                    onAddLocationCancel={startup.cancelAddLocation}
                    onAddLocationOpen={startup.openAddLocation}
                    onApiKeyChange={startup.setApiKey}
                    onApiKeySubmit={startup.submitLocation}
                    onChangeEmail={() => startup.setStep(STARTUP_STEPS.email)}
                    onContinue={startup.continueToApp}
                    onRemoveLocation={startup.removeLocation}
                    onSelectLocation={startup.selectLocation}
                    selectedLocationId={startup.snapshot.selectedLocationId}
                  />
                )}
              </Space>
            </Card>
          </Space>
        </Flex>
      </Layout.Content>
    </Layout>
  );
}
